import React, { useState, useMemo, useCallback, useEffect, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

const AlertLevelConfig = {
    error: { color: '#C00000', bgColor: '#ffebee', label: 'Ошибки', icon: '❌' },
    warning: { color: '#BF8F00', bgColor: '#fff8e1', label: 'Предупреждения', icon: '⚠️' },
    info: { color: '#2E74B5', bgColor: '#e3f2fd', label: 'Инфо', icon: 'ℹ️' },
}

const AlertTypeLabels = {
    TEACHER_OVERLAP: 'Преподаватель',
    GROUP_OVERLAP: 'Группа',
    ROOM_OVERLAP: 'Аудитория',
}

const ALERT_TYPE_FILTERS = [
    { id: 'all', label: 'Все' },
    { id: 'TEACHER_OVERLAP', label: 'Преподаватель' },
    { id: 'GROUP_OVERLAP', label: 'Группа' },
    { id: 'ROOM_OVERLAP', label: 'Аудитория' },
]

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 999;
    animation: fadeIn 0.2s ease;
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`

const Panel = styled.div`
    position: fixed;
    top: 70px;
    right: 20px;
    width: 480px;
    max-height: 80vh;
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    animation: slideDown 0.25s ease;
    @keyframes slideDown {
        from { opacity: 0; transform: translateY(-10px) scale(0.98); }
        to { opacity: 1; transform: translateY(0) scale(1); }
    }
    @media (max-width: 600px) {
        width: 100%; right: 0; top: 0; max-height: 100vh; border-radius: 0;
    }
`

const Header = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 20px; border-bottom: 1px solid #e8edf4; flex-shrink: 0;
`
const HeaderTitle = styled.div` font-size: 18px; font-weight: 700; color: #1f2d3d; `
const HeaderActions = styled.div` display: flex; align-items: center; gap: 12px; `
const CloseButton = styled.button`
    background: none; border: none; font-size: 24px; cursor: pointer;
    color: #8aa4c8; padding: 4px 8px; border-radius: 8px; transition: background 0.2s;
    &:hover { background: #f0f5fc; }
`

const Filters = styled.div`
    display: flex; gap: 8px; padding: 12px 20px;
    border-bottom: 1px solid #e8edf4; flex-wrap: wrap; flex-shrink: 0;
`
const FilterChip = styled.button`
    padding: 6px 14px; border-radius: 20px;
    border: 1px solid ${({ $active }) => ($active ? '#4a6fb0' : '#dce8f7')};
    background: ${({ $active }) => ($active ? '#eef4fd' : 'white')};
    color: ${({ $active }) => ($active ? '#4a6fb0' : '#6888b0')};
    font-weight: 600; font-size: 13px; cursor: pointer; transition: all 0.2s;
    &:hover {
        background: ${({ $active }) => ($active ? '#eef4fd' : '#f6f9fe')};
        transform: translateY(-1px);
    }
`
const FilterCount = styled.span` margin-left: 4px; font-weight: 400; opacity: 0.7; `

const List = styled.div`
    overflow-y: auto; padding: 8px 0; flex: 1;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background-color: #d1d1d1; border-radius: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
`

const AlertCard = styled.div`
    padding: 14px 20px; margin: 4px 12px; border-radius: 12px;
    border-left: 4px solid ${({ $level }) => AlertLevelConfig[$level].color};
    background: white; cursor: pointer; transition: all 0.2s; position: relative;
    &:hover { background: #f0f5fc; transform: translateX(4px); }
`

const AlertCardHeader = styled.div`
    display: flex; align-items: center; gap: 10px; margin-bottom: 4px;
`
const AlertLevelIcon = styled.span`
    color: ${({ $level }) => AlertLevelConfig[$level].color}; font-size: 16px;
`
const AlertTitle = styled.div`
    font-size: 14px; font-weight: 600; color: #1f2d3d; flex: 1;
`

const AlertMessage = styled.div`
    font-size: 13px; color: #5f7495; margin-bottom: 6px;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`

const AlertMeta = styled.div`
    display: flex; align-items: center; gap: 12px; font-size: 12px; color: #8aa4c8; flex-wrap: wrap;
`
const AlertType = styled.span`
    background: #f0f5fc; padding: 2px 10px; border-radius: 12px; color: #4a6fb0; font-weight: 500;
`

const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 60px 20px; color: #8aa4c8;
    div { font-size: 48px; margin-bottom: 16px; }
    h3 { margin: 0 0 8px; color: #1f2d3d; font-weight: 600; }
    p { margin: 0; font-size: 14px; }
`
const LoadingState = styled.div` padding: 40px; text-align: center; color: #8aa4c8; `


const AlertCardItem = memo(({ alert, onClick }) => {
    const handleClick = useCallback(() => onClick(alert), [alert, onClick])

    const formatDate = (dateString) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    }

    const formatTime = (dateString) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        const diff = Date.now() - date.getTime()
        const minutes = Math.floor(diff / 60000)
        if (minutes < 1) return 'только что'
        if (minutes < 60) return `${minutes} мин назад`
        if (minutes < 1440) return `${Math.floor(minutes / 60)} ч назад`
        return `${Math.floor(minutes / 1440)} д назад`
    }

    const classDetails = useMemo(() => {
        if (!alert.class_ids || alert.class_ids.length === 0) return null

        const groupedByDate = {}
        alert.class_ids.forEach(cls => {
            const date = cls.date
            if (!groupedByDate[date]) groupedByDate[date] = []
            groupedByDate[date].push(cls)
        })

        const sortedDates = Object.keys(groupedByDate).sort()

        return (
            <div style={{ 
                marginTop: '8px', 
                fontSize: '12px', 
                color: '#5f7495', 
                borderTop: '1px solid #e8edf4', 
                paddingTop: '8px' 
            }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                    Занятия ({alert.class_ids.length} шт.):
                </div>
                {sortedDates.map(date => (
                    <div key={date} style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: 500, color: '#4a6fb0', marginBottom: '2px' }}>
                            {formatDate(date)}
                        </div>
                        <div style={{ paddingLeft: '4px' }}>
                            {groupedByDate[date].map(cls => (
                                <div 
                                    key={cls.id} 
                                    style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '50px 1fr',
                                        gap: '4px 8px', 
                                        lineHeight: '1.5' 
                                    }}
                                >
                                    <span style={{ color: '#8aa4c8', whiteSpace: 'nowrap' }}>
                                        {cls.bell_number} пара
                                    </span>
                                    <span>
                                        {cls.subject_name || 'Предмет не указан'}
                                        {cls.room_name && <span style={{ color: '#8aa4c8', marginLeft: '6px' }}>({cls.room_name})</span>}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        )
    }, [alert.class_ids])

    return (
        <AlertCard $level={alert.level} onClick={handleClick}>
            <AlertCardHeader>
                <AlertLevelIcon $level={alert.level}>
                    {AlertLevelConfig[alert.level].icon}
                </AlertLevelIcon>
                <AlertTitle>
                    {alert.target?.name || `ID: ${alert.target?.id}`}
                </AlertTitle>
            </AlertCardHeader>

            <AlertMessage>{alert.message}</AlertMessage>

            <AlertMeta>
                <span>{formatTime(alert.created_at)}</span>
                <AlertType>{AlertTypeLabels[alert.type] || alert.type}</AlertType>
            </AlertMeta>

            {classDetails}
        </AlertCard>
    )
})

export const AlertsPanel = ({ onClose, alerts = [], loading = false }) => {
    const navigate = useNavigate()
    const [filterType, setFilterType] = useState('all')
    const [isRendered, setIsRendered] = useState(false)

    useEffect(() => {
        if (loading) {
            setIsRendered(false)
            return
        }
        const timer = setTimeout(() => setIsRendered(true), 50)
        return () => clearTimeout(timer)
    }, [loading, alerts])

    const filteredAlerts = useMemo(() => {
        if (!alerts || alerts.length === 0) return []
        if (filterType === 'all') return alerts
        return alerts.filter((alert) => alert.type === filterType)
    }, [alerts, filterType])

    const getFilterCount = (type) => {
        if (!alerts || alerts.length === 0) return 0
        if (type === 'all') return alerts.length
        return alerts.filter((alert) => alert.type === type).length
    }

    const handleAlertClick = useCallback((alert) => {
        const classIds = (alert.class_ids || [])
            .map((cls) => cls?.id)
            .filter((id) => id !== undefined && id !== null && id !== '')

        if (classIds.length === 0) return

        navigate(`/editor?classes=${classIds.join(',')}`)
        onClose?.()
    }, [navigate, onClose])

    if (loading || !isRendered) {
        return (
            <>
                <Overlay onClick={onClose} />
                <Panel>
                    <Header>
                        <HeaderTitle>Алерты</HeaderTitle>
                        <HeaderActions><CloseButton onClick={onClose}>✕</CloseButton></HeaderActions>
                    </Header>
                    <LoadingState>Загрузка списка...</LoadingState>
                </Panel>
            </>
        )
    }

    return (
        <>
            <Overlay onClick={onClose} />
            <Panel>
                <Header>
                    <HeaderTitle>Алерты</HeaderTitle>
                    <HeaderActions>
                        <CloseButton onClick={onClose}>✕</CloseButton>
                    </HeaderActions>
                </Header>

                <Filters>
                    {ALERT_TYPE_FILTERS.map((filter) => (
                        <FilterChip
                            key={filter.id}
                            $active={filterType === filter.id}
                            onClick={() => setFilterType(filter.id)}
                        >
                            {filter.label} <FilterCount>{getFilterCount(filter.id)}</FilterCount>
                        </FilterChip>
                    ))}
                </Filters>

                <List>
                    {filteredAlerts.length === 0 ? (
                        <EmptyState>
                            <div>✅</div>
                            <h3>Конфликтов не обнаружено</h3>
                            <p>Все проверки пройдены успешно</p>
                        </EmptyState>
                    ) : (
                        filteredAlerts.map((alert, index) => (
                            <AlertCardItem
                                key={`${alert.target?.id || index}-${alert.type}-${alert.created_at}`}
                                alert={alert}
                                onClick={handleAlertClick}
                            />
                        ))
                    )}
                </List>
            </Panel>
        </>
    )
}